import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  LuX,
  LuMail,
  LuCalendar,
  LuClock,
  LuPaperclip,
  LuTrash2,
  LuSend,
  LuCircleCheck,
  LuCircleAlert,
  LuListFilter,
  LuFileText
} from 'react-icons/lu';
import ModalPortal from './ui/ModalPortal';

const EmailReminderModal = ({ isOpen, onClose, recipient }) => {
  const [activeTab, setActiveTab] = useState('create'); // 'create' | 'history'
  const [subject, setSubject] = useState('');
  const [ccEmails, setCcEmails] = useState('');
  const [body, setBody] = useState('');
  const [scheduleType, setScheduleType] = useState('immediate'); // 'immediate', 'custom', 'daily', 'weekly', 'monthly'
  const [customDateTime, setCustomDateTime] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [remindersHistory, setRemindersHistory] = useState([]);
  const [fetchingHistory, setFetchingHistory] = useState(false);

  useEffect(() => {
    if (isOpen && recipient?.email) {
      setSubject(`Reminder: Follow up for ${recipient.name || 'your inquiry'}`);
      setCcEmails('');
      setBody(`Dear ${recipient.name || 'Valued Client'},\n\nThis is a quick reminder regarding your recent submission/inquiry with BooksComply.\n\nPlease let us know if you have any questions or require further assistance.\n\nBest regards,\nBooksComply Team`);
      setError('');
      setSuccessMsg('');
      setAttachments([]);
      setScheduleType('immediate');
      setCustomDateTime('');
      setActiveTab('create');
      fetchRemindersHistory();
    }
  }, [isOpen, recipient]);

  const fetchRemindersHistory = async () => {
    if (!recipient?.email) return;
    setFetchingHistory(true);
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/contact-reminders?email=${encodeURIComponent(recipient.email)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setRemindersHistory(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch reminder history', err);
    } finally {
      setFetchingHistory(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAttachments((prev) => [...prev, ...newFiles]);
    }
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      setError('Please provide both subject and message body.');
      return;
    }

    if (scheduleType !== 'immediate' && !customDateTime) {
      setError('Please select a date & time for scheduled reminder.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMsg('');

    const token = localStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('recipientName', recipient?.name || recipient?.email);
    formData.append('recipientEmail', recipient?.email);
    if (ccEmails.trim()) {
      formData.append('ccEmails', ccEmails.trim());
    }
    formData.append('subject', subject);
    formData.append('body', body);
    formData.append('scheduleType', scheduleType);

    if (customDateTime) {
      formData.append('customDateTime', customDateTime);
    }

    attachments.forEach((file) => {
      formData.append('attachments', file);
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/contact-reminders`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        setSuccessMsg(response.data.message || 'Reminder set successfully!');
        fetchRemindersHistory();
        setTimeout(() => {
          if (scheduleType === 'immediate') {
            onClose();
          } else {
            setActiveTab('history');
          }
        }, 1200);
      }
    } catch (err) {
      console.error('Error setting reminder:', err);
      setError(err.response?.data?.message || 'Failed to send/schedule reminder.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReminder = async (id) => {
    if (!window.confirm('Are you sure you want to cancel/delete this reminder?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/contact-reminders/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchRemindersHistory();
    } catch (err) {
      alert('Failed to delete reminder');
    }
  };

  if (!isOpen || !recipient) return null;

  return (
    <ModalPortal open={isOpen} onClose={onClose}>
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-6 text-white flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-white/10 rounded-2xl backdrop-blur-md">
            <LuMail className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Email & Reminder Center</h2>
            <p className="text-xs text-primary-100">
              To: <span className="font-semibold text-white">{recipient.name}</span> ({recipient.email})
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 hover:bg-white/10 rounded-xl transition-colors text-white/80 hover:text-white cursor-pointer"
        >
          <LuX className="w-5 h-5" />
        </button>
      </div>

      {/* Tab Selection */}
      <div className="flex border-b border-gray-100 dark:border-dark-700 bg-gray-50/50 dark:bg-dark-700/30 px-6 pt-3 gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => setActiveTab('create')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-xs font-bold transition-all border-b-2 cursor-pointer ${activeTab === 'create'
              ? 'border-primary-600 text-primary-600 bg-white dark:bg-dark-800 shadow-sm'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
        >
          <LuSend className="w-4 h-4" />
          Set New Reminder
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('history')}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-xs font-bold transition-all border-b-2 cursor-pointer ${activeTab === 'history'
              ? 'border-primary-600 text-primary-600 bg-white dark:bg-dark-800 shadow-sm'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
            }`}
        >
          <LuListFilter className="w-4 h-4" />
          Scheduled Reminders ({remindersHistory.length})
        </button>
      </div>

      {/* Tab Contents */}
      <div className="p-6 overflow-y-auto flex-1 space-y-4">
        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl text-red-700 dark:text-red-300 text-xs font-medium">
            <LuCircleAlert className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {successMsg && (
          <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-2xl text-green-700 dark:text-green-300 text-xs font-medium">
            <LuCircleCheck className="w-5 h-5 flex-shrink-0" />
            <span>{successMsg}</span>
          </div>
        )}

        {activeTab === 'create' ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Recipient Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 block mb-1">
                  Recipient Name
                </label>
                <input
                  type="text"
                  value={recipient.name || ''}
                  readOnly
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 block mb-1">
                  Recipient Email
                </label>
                <input
                  type="email"
                  value={recipient.email || ''}
                  readOnly
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm font-semibold text-primary-600 dark:text-primary-400 cursor-not-allowed"
                />
              </div>
            </div>

            {/* CC Email Input */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 block mb-1">
                CC Email(s) (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g. support@bookscomply.com, manager@bookscomply.com"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/50 outline-none transition-all font-medium"
              />
            </div>

            {/* Subject */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 block mb-1">
                Email Subject *
              </label>
              <input
                type="text"
                placeholder="Enter email subject..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/50 outline-none transition-all font-medium"
              />
            </div>

            {/* Body */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 block mb-1">
                Message Body *
              </label>
              <textarea
                rows={5}
                placeholder="Write your email reminder message here..."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500/50 outline-none transition-all font-normal leading-relaxed"
              />
            </div>

            {/* Schedule / Frequency Selection */}
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 block mb-1.5">
                Schedule Type & Frequency
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {[
                  { key: 'immediate', label: 'Send Now', icon: LuSend },
                  { key: 'custom', label: 'Custom Date', icon: LuCalendar },
                  { key: 'daily', label: 'Daily', icon: LuClock },
                  { key: 'weekly', label: 'Weekly', icon: LuClock },
                  { key: 'monthly', label: 'Monthly', icon: LuClock },
                ].map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setScheduleType(option.key)}
                    className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${scheduleType === option.key
                        ? 'bg-primary-50 dark:bg-primary-900/40 border-primary-500 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'bg-white dark:bg-dark-700 border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50'
                      }`}
                  >
                    <option.icon className="w-4 h-4 mb-1" />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Date & Time Picker */}
            {scheduleType !== 'immediate' && (
              <div className="animate-in fade-in duration-200 p-4 bg-primary-50/50 dark:bg-primary-900/20 rounded-2xl border border-primary-100 dark:border-primary-800/50">
                <label className="text-[10px] font-black uppercase tracking-wider text-primary-700 dark:text-primary-300 block mb-1">
                  Select Date & Time for Reminder
                </label>
                <input
                  type="datetime-local"
                  value={customDateTime}
                  onChange={(e) => setCustomDateTime(e.target.value)}
                  required={scheduleType !== 'immediate'}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500/50"
                />
              </div>
            )}

            {/* Attachments Section */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 block">
                  File Attachments (Optional)
                </label>
                <label className="text-xs font-bold text-primary-600 dark:text-primary-400 hover:underline cursor-pointer flex items-center gap-1">
                  <LuPaperclip className="w-3.5 h-3.5" />
                  <span>Attach Files</span>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              </div>

              {attachments.length > 0 ? (
                <div className="space-y-2 max-h-36 overflow-y-auto">
                  {attachments.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700/60 border border-gray-200 dark:border-dark-600 text-xs"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <LuFileText className="w-4 h-4 text-primary-500 flex-shrink-0" />
                        <span className="font-semibold text-gray-800 dark:text-gray-200 truncate">{file.name}</span>
                        <span className="text-[10px] text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(idx)}
                        className="p-1 hover:bg-red-50 text-red-500 rounded-lg transition-colors"
                      >
                        <LuX className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 border-2 border-dashed border-gray-200 dark:border-dark-600 rounded-2xl text-center">
                  <p className="text-xs text-gray-400">No files attached yet. You can attach PDFs, images, or documents.</p>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-2 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 text-xs font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-bold transition-all shadow-lg shadow-primary-500/30 flex items-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                <LuSend className="w-4 h-4" />
                {loading
                  ? 'Processing...'
                  : scheduleType === 'immediate'
                    ? 'Send Email Reminder Now'
                    : 'Schedule Reminder'}
              </button>
            </div>
          </form>
        ) : (
          /* History & Scheduled Reminders View */
          <div className="space-y-3">
            {fetchingHistory ? (
              <div className="text-center py-10 text-xs text-gray-400 font-semibold">Loading reminders history...</div>
            ) : remindersHistory.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-100 dark:border-dark-700 rounded-2xl">
                <LuMail className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-xs font-bold text-gray-500">No active or past reminders found</p>
                <p className="text-[11px] text-gray-400 mt-1">Use the "Set New Reminder" tab to create one.</p>
              </div>
            ) : (
              remindersHistory.map((item) => (
                <div
                  key={item._id}
                  className="p-4 rounded-2xl border border-gray-100 dark:border-dark-700 bg-gray-50/60 dark:bg-dark-700/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm text-gray-900 dark:text-white">{item.subject}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${item.status === 'sent'
                            ? 'bg-green-100 text-green-700 border border-green-200'
                            : item.status === 'pending'
                              ? 'bg-blue-100 text-blue-700 border border-blue-200'
                              : 'bg-red-100 text-red-700 border border-red-200'
                          }`}
                      >
                        {item.status}
                      </span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-50 text-purple-600 border border-purple-100 uppercase">
                        {item.scheduleType || 'immediate'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 italic">"{item.body}"</p>
                    {item.ccEmails && item.ccEmails.length > 0 && (
                      <p className="text-[10px] text-gray-400 font-medium">CC: {item.ccEmails.join(', ')}</p>
                    )}
                    <div className="text-[10px] text-gray-400 flex items-center gap-3 pt-1">
                      <span>Scheduled: {new Date(item.scheduledAt).toLocaleString()}</span>
                      {item.attachments?.length > 0 && <span>📎 {item.attachments.length} attachment(s)</span>}
                    </div>
                  </div>

                  <button
                    onClick={() => handleDeleteReminder(item._id)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl transition-colors cursor-pointer flex-shrink-0"
                    title="Delete Reminder"
                  >
                    <LuTrash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </ModalPortal>
  );
};

export default EmailReminderModal;
